import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Zap, Brain } from 'lucide-react';

const Hero = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Set launch date to 30 days from now
    const launchDate = new Date();
    launchDate.setDate(launchDate.getDate() + 30);

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center space-x-2 bg-primary-500/10 border border-primary-500/20 rounded-full px-4 py-2">
            <Sparkles className="w-4 h-4 text-primary-400" />
            <span className="text-primary-400 text-sm font-medium">AI Automation Consulting</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 
            variants={itemVariants}
            className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
          >
            <span className="text-white">The Future of</span>
            <br />
            <span className="text-gradient">AI Automation</span>
            <br />
            <span className="text-white">is Coming</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            variants={itemVariants}
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            Expert consulting services to help you plan, design, and implement AI automation solutions. 
            From strategic planning to vendor evaluation, we guide you every step of the way.
          </motion.p>

          {/* 3D Countdown Timer */}
          <motion.div variants={itemVariants} className="flex justify-center">
            <div className="grid grid-cols-4 gap-4 md:gap-8 perspective-1000">
              {Object.entries(timeLeft).map(([unit, value], index) => (
                <motion.div 
                  key={unit} 
                  className="text-center"
                  whileHover={{ 
                    scale: 1.1,
                    rotateX: 10,
                    rotateY: 10,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="bg-glass-effect rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] card-3d depth-shadow">
                    <div className="text-2xl md:text-4xl font-bold text-white">
                      {value.toString().padStart(2, '0')}
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 uppercase tracking-wider mt-1">
                      {unit}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>



          {/* Stats */}
          <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-8 pt-8">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">100+</div>
              <div className="text-gray-400 text-sm">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">95%</div>
              <div className="text-gray-400 text-sm">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">24/7</div>
              <div className="text-gray-400 text-sm">Expert Support</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Optimized 3D Floating elements */}
      <div className="absolute top-1/4 left-10 opacity-20 floating-3d">
        <Zap className="w-8 h-8 text-primary-400" />
      </div>
      <div className="absolute bottom-1/4 right-10 opacity-20 floating-3d" style={{ animationDelay: '2s' }}>
        <Brain className="w-8 h-8 text-purple-400" />
      </div>
      <div className="absolute top-1/3 right-1/4 opacity-15 rotate-3d">
        <div className="w-6 h-6 bg-gradient-to-r from-primary-400 to-purple-400 rounded-full"></div>
      </div>
      <div className="absolute bottom-1/3 left-1/4 opacity-15 rotate-3d" style={{ animationDelay: '3s' }}>
        <div className="w-4 h-4 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"></div>
      </div>
    </section>
  );
};

export default Hero; 